import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

import type { DeviceCodeRequest, ApiError } from '@/types';
import { ErrorService } from '@/error/error.service';
import { errorCodeOf, getJson } from '@/utils/http';

export class DeviceFlowService {
  public async getDeviceRequest(userCode: string | null): Promise<DeviceCodeRequest | ApiError> {
    try {
      return await getJson<DeviceCodeRequest>(`/device/setup/${userCode}`);
    } catch (e) {
      return ErrorService.getError('ibis', errorCodeOf(e));
    }
  }
}

const DEVICE_CLIENT_STORAGE_KEY = 'deviceFlowClient';

interface DeviceClient {
  authority: string;
  clientId: string;
  scopes: string;
}

/**
 * oidc-client-ts requires `authority` and `client_id` up front, but at the
 * consent callback they are only discoverable *after* the device request is
 * fetched - which itself needs the user code from the callback. The login step
 * therefore persists them so the callback can rebuild an identical UserManager.
 */
export function storeDeviceClient(client: DeviceClient): void {
  localStorage.setItem(DEVICE_CLIENT_STORAGE_KEY, JSON.stringify(client));
}

export function loadDeviceClient(): DeviceClient | null {
  const stored = localStorage.getItem(DEVICE_CLIENT_STORAGE_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored) as DeviceClient;
  } catch {
    return null;
  }
}

export function createDeviceUserManager(client: DeviceClient): UserManager {
  return new UserManager({
    authority: client.authority,
    client_id: client.clientId,
    redirect_uri: `${client.authority}/ui/device/callback`,
    response_type: 'code',
    response_mode: 'query',
    scope: client.scopes,
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });
}
